import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  PdfColor,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import { moduleRegistry } from '../../modules';
import { diffModule } from '../../modules/diff';
import { ModuleParams } from '../../modules/types';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);
const LABEL_H = MM(6);
const GAP = MM(5);
const CONTENT_W = PdfSize.A4.width - 2 * MARGIN;

export class TestDiff implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    moduleRegistry.register(diffModule);
    const diff = moduleRegistry.get('diff');
    if (!diff) throw new Error('Diff module not found');

    const font = new PdfFont(PdfFont.FONT_NAME, 10);
    const labelFont = new PdfFont(PdfFont.FONT_NAME, 10);
    const titleFont = new PdfFont(PdfFont.FONT_NAME, 14);
    const subtitleFont = new PdfFont(PdfFont.FONT_NAME, 9);
    const grayBrush = new PdfBrush(PdfColor.fromRgb(100, 100, 100));

    // Helper: parse, measure, draw — returns actual height used.
    const drawDiff = (page: PageGen, x: number, y: number, w: number, content: string): number => {
      const parsed = diff.parse(content, {});
      const params: ModuleParams = { font, page };
      const size = diff.measure(parsed, params);
      const rect = new PdfRect(x, y, w, size.height);
      diff.draw(parsed, rect, ETextAlign.TopLeft, params);
      return size.height;
    };

    // Helper: draw label + diff block, advance yPos.
    const section = (
      page: PageGen,
      label: string,
      content: string,
      yPos: number,
      w = CONTENT_W
    ): number => {
      page.drawString(
        label,
        labelFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos, w, LABEL_H),
        ETextAlign.TopLeft
      );
      yPos += LABEL_H;
      yPos += drawDiff(page, MARGIN, yPos, w, content);
      yPos += GAP;
      return yPos;
    };

    // ── PAGE 1: Inline mode ───────────────────────────────────────────────────
    const page1 = pdf.addPage();
    page1.drawString(
      'Diff Module Test — Inline Mode (Page 1)',
      titleFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(5), CONTENT_W, MM(8)),
      ETextAlign.CenterLeft
    );

    let y = MM(18);

    y = section(
      page1,
      '1. Simple additions:',
      `<diff>
<left>
line 1
line 2
</left>
<right>
line 1
line 2
line 3
</right>
</diff>`,
      y
    );

    y = section(
      page1,
      '2. Simple removals:',
      `<diff>
<left>
line a
line b
line c
</left>
<right>
line a
line c
</right>
</diff>`,
      y
    );

    y = section(
      page1,
      '3. Modified line (char diff):',
      `<diff>
<left>
print "Hello world!"
exit 0
</left>
<right>
printf "HELLO!"
exit 0
</right>
</diff>`,
      y
    );

    y = section(
      page1,
      '4. Small text change:',
      `<diff>
<left>
Hello World
Test Line
</left>
<right>
Hello There
Test Line
</right>
</diff>`,
      y
    );

    y = section(
      page1,
      '5. Replaced line:',
      `<diff>
<left>
old function name
another line
</left>
<right>
newFunctionName
another line
</right>
</diff>`,
      y
    );

    y = section(
      page1,
      '6. Add + Remove on same line:',
      `<diff>
<left>
The quick brown fox
</left>
<right>
The fast brown dog
</right>
</diff>`,
      y
    );

    y = section(
      page1,
      '7. Code refactor:',
      `<diff>
<left>
function oldCalc(x, y) {
  return x + y;
}
</left>
<right>
function newCalc(a, b) {
  const result = a + b;
  return result;
}
</right>
</diff>`,
      y
    );

    // ── PAGE 2: More inline tests ─────────────────────────────────────────────
    const page2 = pdf.addPage();
    page2.drawString(
      'Diff Module Test — Inline Mode (Page 2)',
      titleFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(5), CONTENT_W, MM(8)),
      ETextAlign.CenterLeft
    );

    y = MM(18);

    y = section(
      page2,
      '8. Empty left (all adds):',
      `<diff>
<left>

</left>
<right>
new line 1
new line 2
new line 3
</right>
</diff>`,
      y
    );

    y = section(
      page2,
      '9. Empty right (all removes):',
      `<diff>
<left>
line to remove
another gone
</left>
<right>

</right>
</diff>`,
      y
    );

    y = section(
      page2,
      '10. Whitespace changes:',
      `<diff>
<left>
text with spaces
</left>
<right>
text	with tabs
</right>
</diff>`,
      y
    );

    y = section(
      page2,
      '11. Case changes:',
      `<diff>
<left>
HELLO World
</left>
<right>
hello world
</right>
</diff>`,
      y
    );

    y = section(
      page2,
      '12. Punctuation changes:',
      `<diff>
<left>
Hello, World!
</left>
<right>
Hello World?
</right>
</diff>`,
      y
    );

    // ── PAGE 3: Split mode ────────────────────────────────────────────────────
    const page3 = pdf.addPage();
    page3.drawString(
      'Diff Module Test — Split Mode (Page 3)',
      titleFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(5), CONTENT_W, MM(8)),
      ETextAlign.CenterLeft
    );

    y = MM(18);

    y = section(
      page3,
      '1. Split – additions:',
      `<diff mode="split">
<left>
line 1
line 2
</left>
<right>
line 1
line 2
line 3
</right>
</diff>`,
      y
    );

    y = section(
      page3,
      '2. Split – char diff:',
      `<diff mode="split">
<left>
print "Hello world!"
exit 0
</left>
<right>
printf "HELLO!"
exit 0
</right>
</diff>`,
      y
    );

    y = section(
      page3,
      '3. Split – small change:',
      `<diff mode="split">
<left>
Hello World
Test Line
</left>
<right>
Hello There
Test Line
</right>
</diff>`,
      y
    );

    y = section(
      page3,
      '4. Split – add & remove:',
      `<diff mode="split">
<left>
The quick brown fox
</left>
<right>
The fast brown dog
</right>
</diff>`,
      y
    );

    y = section(
      page3,
      '5. Split – code refactor:',
      `<diff mode="split">
<left>
function oldCalc(x, y) {
  return x + y;
}
</left>
<right>
function newCalc(a, b) {
  const result = a + b;
  return result;
}
</right>
</diff>`,
      y
    );

    y = section(
      page3,
      '6. Split – complex mixed:',
      `<diff mode="split">
<left>
start line
keep this
remove this
also keep
end line
</left>
<right>
start line
keep this
add new line
modify this
also keep
end line
</right>
</diff>`,
      y
    );

    // ── PAGE 4: Line-diff only (char-diff="false") ────────────────────────────
    const page4 = pdf.addPage();
    page4.drawString(
      'Diff Module Test — Line Diff Only (Page 4)',
      titleFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(5), CONTENT_W, MM(8)),
      ETextAlign.CenterLeft
    );
    page4.drawString(
      '(char-diff="false" — no character-level highlighting)',
      subtitleFont,
      grayBrush,
      null,
      new PdfRect(MARGIN, MM(13), CONTENT_W, MM(5)),
      ETextAlign.CenterLeft
    );

    y = MM(22);

    y = section(
      page4,
      '1. Inline, line-diff only – modified lines:',
      `<diff char-diff="false">
<left>
print "Hello world!"
exit 0
</left>
<right>
printf "HELLO!"
exit 0
</right>
</diff>`,
      y
    );

    y = section(
      page4,
      '2. Inline, line-diff only – code refactor:',
      `<diff char-diff="false">
<left>
function oldCalc(x, y) {
  return x + y;
}
</left>
<right>
function newCalc(a, b) {
  const result = a + b;
  return result;
}
</right>
</diff>`,
      y
    );

    y = section(
      page4,
      '3. Inline, line-diff only – complex mixed:',
      `<diff char-diff="false">
<left>
start line
keep this
remove this
also keep
end line
</left>
<right>
start line
keep this
add new line
modify this
also keep
end line
</right>
</diff>`,
      y
    );

    y = section(
      page4,
      '4. Split, line-diff only – code refactor:',
      `<diff mode="split" char-diff="false">
<left>
function oldCalc(x, y) {
  return x + y;
}
</left>
<right>
function newCalc(a, b) {
  const result = a + b;
  return result;
}
</right>
</diff>`,
      y
    );
  }
}
